<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateProperty',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const resource = ref(null);
  const editor = ref(null);
  const container = ref(null);
  const formName = ref('meter');
  const formId = ref(null);
  const propertyUnitTypes = ref(null);
  const units = ref(['ha', 'm2']);
  const resourceTitle = ref($fn.value.pgettext('Property', 'Property'));
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    container: container.value,
    formName: formName.value,
    formId: formId.value,
    propertyUnitTypes: propertyUnitTypes.value,
    units: units.value,
    resourceTitle: resourceTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = () => {
    return $fn.value.schemaDefaults('CreateProperty', { owner: null });
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };
  const $$load_container = () => {
    return {
      self: $fn.value.urlFor('create_property'),
      object: 'list',
      data: [],
    };
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_propertyUnitTypes = () => {
    return $fn.value
      .fetch('list_property_types')
      .then((p) => _.orderBy(p, ['name']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'container' },
      { name: 'formName' },
      { name: 'formId' },
      { name: 'propertyUnitTypes' },
      { name: 'units' },
      { name: 'resourceTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      resource: $$load_resource,
      editor: $$load_editor,
      container: $$load_container,
      formId: $$load_formId,
      propertyUnitTypes: $$load_propertyUnitTypes,
    },
    ux: {},
    dataRefs: {
      resource,
      editor,
      container,
      formName,
      formId,
      propertyUnitTypes,
      units,
      resourceTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.pgettext('Property', 'New property') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_osc0kibmh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_wbpn5hadp_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_rd3jepyfu_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.property',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadGrid_w0glrtg6k_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'properties',
    };
  };
  const act_1040167445267876_rest_wkfdilh3s_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_osc0kibmh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_osc0kibmh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_0j12cl0w9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_osc0kibmh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_0j12cl0w9],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_smju2hd6f =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_2gi9att9i =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_smju2hd6f,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_2gi9att9i],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_1ovqfr72y =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_zodu1v2yf =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_1ovqfr72y,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_zodu1v2yf],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_wbpn5hadp =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_wbpn5hadp_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_lg7g6yzt0 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_wbpn5hadp,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_navigate_rd3jepyfu =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_rd3jepyfu_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_gqxb4ctmh =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_rd3jepyfu,
      event: 'saved',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadGrid_w0glrtg6k =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_w0glrtg6k_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_t3kkrexa4 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_w0glrtg6k,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_rest_wkfdilh3s = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_wkfdilh3s_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_saved_lg7g6yzt0,
      inst_evh_1040167445267876_saved_gqxb4ctmh,
      inst_evh_1040167445267876_saved_t3kkrexa4,
    ],
  });
  const inst_evh_1040167445267876_submit_b9zoz47e7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_wkfdilh3s,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_b9zoz47e7],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_aotb1lezw =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_foatwmm28 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_aotb1lezw,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_foatwmm28],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_0j12cl0w9,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_2gi9att9i,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_zodu1v2yf,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_lg7g6yzt0,
    inst_evh_1040167445267876_saved_gqxb4ctmh,
    inst_evh_1040167445267876_saved_t3kkrexa4,
    inst_evh_1040167445267876_submit_b9zoz47e7,
    create: inst_evh_1040167445267876_submit_b9zoz47e7,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_foatwmm28,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_0j12cl0w9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_0j12cl0w9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_2gi9att9i = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_2gi9att9i.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_zodu1v2yf = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_zodu1v2yf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_lg7g6yzt0 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_lg7g6yzt0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_gqxb4ctmh = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_gqxb4ctmh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_t3kkrexa4 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_t3kkrexa4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_b9zoz47e7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_b9zoz47e7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_foatwmm28 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_foatwmm28.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateProperty"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :dialogName="resourceTitle"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateProperty"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetPropertyGeneralData :property="editor">
        </FieldsetPropertyGeneralData>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="forms[formName]"
          :spinning="actions.create.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
